// Nieuwsbrief voor de Dif3r3nt Store: producten, prijzen, foto's en categorieën komen live uit de Store
// (dezelfde database als de OS), de opmaak is de Dif3r3nt-huisstijl met het D3-logo. Alles in de mail is klikbaar.
// Verzenden gebeurt hier niet: je kopieert of downloadt de HTML en zet hem in je mailtool.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabase";
import { C, lees, Kop, Stat, Melding } from "./gedeeld";
import { bouwNieuwsbrief, ongeldigeLinks, prijsVan, eurTekst, kortTitel, STORE_URL } from "./nieuwsbriefHtml";

const LOGO_URL = STORE_URL + "/logo3.png";
const STANDAARD = {
  label: "Nieuwe collectie drop",
  kop: "In de ban van\nDif3r3nt",
  intro: "Er hangt een nieuwe vibe in de lucht. Maak kennis met onze nieuwste collectie: exclusieve pasvormen en paars-blauwe accenten die jouw stijl een unieke, futuristische twist geven.",
  knop: "Ontdek de collectie",
};
const VOORBEELD_AFMELD = STORE_URL + "/lid/afmelden?token=voorbeeld";

export default function Nieuwsbrief() {
  const [producten, setProducten] = useState([]);
  const [categorieen, setCategorieen] = useState([]);
  const [leden, setLeden] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState("");
  const [gekozen, setGekozen] = useState([]);
  const [tekst, setTekst] = useState(STANDAARD);
  const [melding, setMelding] = useState("");

  useEffect(() => {
    let weg = false;
    (async () => {
      const [p, c, l] = await Promise.all([
        lees(supabase.from("products").select("id,title,sellPrice,kortingPct,kortingTot,imageUrl,category,createdAt").eq("status", "LIVE").order("createdAt", { ascending: false }).limit(60)),
        lees(supabase.from("shop_categories").select("key,label,volgorde").eq("actief", true).order("volgorde")),
        supabase.from("shop_leden").select("id", { count: "exact", head: true }).is("uitgeschrevenOp", null),
      ]);
      if (weg) return;
      if (p.fout) setFout("De producten van de Store konden niet worden opgehaald.");
      const rijen = p.data.map((r) => ({ ...r, foto: r.imageUrl }));
      setProducten(rijen);
      setCategorieen(c.data);
      setLeden(l && typeof l.count === "number" ? l.count : null);
      setGekozen(rijen.filter((r) => r.foto).slice(0, 2).map((r) => r.id));
      setLaden(false);
    })();
    return () => { weg = true; };
  }, []);

  const gekozenProducten = useMemo(() => gekozen.map((id) => producten.find((p) => p.id === id)).filter(Boolean), [gekozen, producten]);
  const html = useMemo(() => bouwNieuwsbrief({ ...tekst, producten: gekozenProducten, categorieen, afmeldUrl: VOORBEELD_AFMELD, email: "klant@dif3r3nt.nl", logoUrl: LOGO_URL }), [tekst, gekozenProducten, categorieen]);
  // Voor het echte verzenden: afmeldlink en e-mailadres blijven plaatshouders die de mailtool per lid invult.
  const htmlVerzenden = useMemo(() => bouwNieuwsbrief({ ...tekst, producten: gekozenProducten, categorieen, afmeldUrl: "{{AFMELD_URL}}", email: "{{EMAIL}}", logoUrl: LOGO_URL }), [tekst, gekozenProducten, categorieen]);
  const kapot = useMemo(() => ongeldigeLinks(htmlVerzenden), [htmlVerzenden]);

  const wissel = (id) => setGekozen((g) => (g.includes(id) ? g.filter((x) => x !== id) : g.length >= 6 ? g : [...g, id]));
  const zet = (k) => (e) => setTekst((t) => ({ ...t, [k]: e.target.value }));
  const flits = (t) => { setMelding(t); setTimeout(() => setMelding(""), 3000); };

  const kopieer = async () => {
    try { await navigator.clipboard.writeText(htmlVerzenden); flits("HTML gekopieerd"); }
    catch { flits("Kopiëren mislukt, gebruik Downloaden"); }
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([htmlVerzenden], { type: "text/html;charset=utf-8" }));
    a.download = "dif3r3nt-store-nieuwsbrief.html";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <div>
      <Kop titel="Nieuwsbrief Store" sub="Dif3r3nt-huisstijl met D3-logo. Producten, prijzen en foto's komen live uit de Store."
        rechts={<>
          <button className="btn bg" onClick={kopieer} disabled={!gekozenProducten.length}>Kopieer HTML</button>
          <button className="btn bp" onClick={download} disabled={!gekozenProducten.length}>Download .html</button>
        </>} />
      {melding && <Melding type="ok">{melding}</Melding>}
      {fout && <Melding type="fout">{fout}</Melding>}
      {kapot.length > 0 && <Melding type="fout">Niet elke link is bruikbaar: {kapot.join(", ")}</Melding>}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <Stat label="Producten live in de Store" waarde={laden ? "…" : producten.length} />
        <Stat label="Gekozen voor de mail" waarde={gekozenProducten.length + " / 6"} kleur={C.blue} />
        <Stat label="Actieve leden (ontvangers)" waarde={leden == null ? "-" : leden} kleur={C.green} sub="Alleen leden die zijn aangemeld" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px,380px) 1fr", gap: 14, alignItems: "start" }}>
        <div>
          <div className="card" style={{ marginBottom: 12 }}>
            <p className="lbl">Tekst</p>
            <p style={{ fontSize: 11, color: C.muted, margin: "8px 0 4px" }}>Bovenregel</p>
            <input value={tekst.label} onChange={zet("label")} />
            <p style={{ fontSize: 11, color: C.muted, margin: "8px 0 4px" }}>Kop (Enter = nieuwe regel, laatste regel krijgt de kleur)</p>
            <textarea rows={2} value={tekst.kop} onChange={zet("kop")} />
            <p style={{ fontSize: 11, color: C.muted, margin: "8px 0 4px" }}>Inleiding</p>
            <textarea rows={4} value={tekst.intro} onChange={zet("intro")} />
            <p style={{ fontSize: 11, color: C.muted, margin: "8px 0 4px" }}>Tekst op de knop</p>
            <input value={tekst.knop} onChange={zet("knop")} />
            <button className="btn bg" style={{ fontSize: 11, marginTop: 10 }} onClick={() => setTekst(STANDAARD)}>Standaardtekst terugzetten</button>
          </div>

          <div className="card">
            <p className="lbl">Producten (kies er 2, 4 of 6)</p>
            {laden ? <p style={{ fontSize: 12, color: C.muted }}>Laden…</p> : producten.length === 0 ? <p style={{ fontSize: 12, color: C.dim }}>Er staan geen producten live in de Store.</p> : (
              <div style={{ maxHeight: 420, overflowY: "auto", marginTop: 8 }}>
                {producten.map((p) => {
                  const aan = gekozen.includes(p.id);
                  const { prijs } = prijsVan(p);
                  return (
                    <label key={p.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "6px 0", borderBottom: "1px solid " + C.border, cursor: p.foto ? "pointer" : "not-allowed", opacity: p.foto ? 1 : 0.45 }}>
                      <input type="checkbox" checked={aan} disabled={!p.foto} onChange={() => wissel(p.id)} style={{ width: "auto" }} />
                      {p.foto ? <img src={p.foto} alt="" width="38" height="38" style={{ objectFit: "cover", borderRadius: 6 }} /> : <span style={{ width: 38, height: 38, background: C.surfaceHigh, borderRadius: 6 }} />}
                      <span style={{ flex: 1, fontSize: 12, minWidth: 0 }}>{kortTitel(p.title, 48)}<br /><span style={{ color: C.muted }}>{eurTekst(prijs)}{p.foto ? "" : " · geen foto"}</span></span>
                    </label>
                  );
                })}
              </div>
            )}
            {gekozenProducten.length % 2 === 1 && <p style={{ fontSize: 11, color: C.yellow, marginTop: 8 }}>Een oneven aantal laat een lege plek naast het laatste product. Kies er een bij of haal er een weg.</p>}
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <p className="lbl" style={{ padding: "12px 14px 0" }}>Voorbeeld (links werken ook hier)</p>
          <iframe title="Voorbeeld nieuwsbrief" srcDoc={html} style={{ width: "100%", height: 1100, border: 0, background: "#0b0b14" }} />
        </div>
      </div>

      <Melding type="info">
        Verzenden: kopieer de HTML naar je mailtool. De plaatshouders <b>{"{{AFMELD_URL}}"}</b> (de persoonlijke afmeldlink van elk lid, <code>{STORE_URL}/lid/afmelden?token=…</code>) en <b>{"{{EMAIL}}"}</b> vult de mailtool per ontvanger in. Gebruik alleen leden die niet zijn uitgeschreven.
      </Melding>
    </div>
  );
}
